import type { DebugConfig, AnimType, SectionKey } from '../config/debug'
import { isMobileDevice } from './appLinks'

const SPEED_DEFAULTS: Record<SectionKey, number> = {
  HERO: 0.5,
  ABOUT: 0.6,
  EXPERIENCE: 0.6,
  ACHIEVEMENTS: 0.5,
  PROJECTS: 0.5,
  SIDE_PROJECTS: 0.5,
  ARCHIVE: 0.6,
  EDUCATION: 0.6,
  RECOMMENDATIONS: 0.5,
  RESUME: 0.5,
  FAQ: 0.5,
}

const TYPE_DEFAULTS: Record<SectionKey, AnimType> = {
  HERO: 'fadeInUp',
  ABOUT: 'fadeIn',
  EXPERIENCE: 'fadeInUp',
  ACHIEVEMENTS: 'fadeInUp',
  PROJECTS: 'fadeInUp',
  SIDE_PROJECTS: 'fadeInUp',
  ARCHIVE: 'fadeIn',
  EDUCATION: 'fadeIn',
  RECOMMENDATIONS: 'fadeInUp',
  RESUME: 'fadeInUp',
  FAQ: 'fadeInUp',
}

export const fadeInUp = (enabled: boolean, delay = 0, duration = 0.5) =>
  enabled
    ? {
        initial: { opacity: 0, y: 20 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true },
        transition: { duration, delay },
      }
    : {}

export const fadeIn = (enabled: boolean, duration = 0.6) =>
  enabled
    ? {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0, transition: { duration } },
        viewport: { once: true },
      }
    : {}

export const scaleIn = (enabled: boolean, delay = 0, duration = 0.5) =>
  enabled
    ? {
        initial: { opacity: 0, scale: 0.95 },
        whileInView: { opacity: 1, scale: 1 },
        viewport: { once: true },
        transition: { duration, delay },
      }
    : {}

export function getAnimProps(
  sectionKey: SectionKey,
  config: DebugConfig,
  overrides?: { delay?: number },
): Record<string, unknown> {
  if (!config.ANIMATIONS || !config.ANIM_SECTIONS?.[sectionKey]) return {}

  const type = config.ANIM_TYPE?.[sectionKey] ?? TYPE_DEFAULTS[sectionKey]
  const speed = isMobileDevice() ? 0.4 : (config.ANIM_SPEED?.[sectionKey] ?? SPEED_DEFAULTS[sectionKey])
  const delay = overrides?.delay ?? 0

  const useSpring = sectionKey === 'EXPERIENCE' && config.EXPERIENCE_SPRING_ANIM === true

  switch (type) {
    case 'fadeInUp':
      return {
        initial: useSpring ? { y: 20 } : { opacity: 0, y: 20 },
        whileInView: useSpring ? { y: 0 } : { opacity: 1, y: 0 },
        viewport: { once: true },
        transition: useSpring
          ? { type: 'spring', stiffness: 350, damping: 15, mass: 0.8, delay }
          : { duration: speed, delay },
      }
    case 'fadeIn':
      return {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0, transition: { duration: speed } },
        viewport: { once: true },
      }
    case 'scaleIn':
      return {
        initial: { opacity: 0, scale: 0.95 },
        whileInView: { opacity: 1, scale: 1 },
        viewport: { once: true },
        transition: { duration: speed, delay },
      }
    case 'none':
      return {}
    default:
      return {}
  }
}
